import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../services/authService';
import { isUploadActive } from '../services/uploadActivity';

interface SessionTimeoutConfig {
  timeoutMinutes: number;
  warningMinutes: number;
}

interface SessionTimeoutState {
  isWarningVisible: boolean;
  remainingSeconds: number;
  isExtended: boolean;
}

const CJIS_TIMEOUT_MINUTES = 30;
const CJIS_WARNING_MINUTES = 5;
const CHECK_INTERVAL_SECONDS = 10;

/**
 * CJIS 5.5.5 Session Timeout Hook
 *
 * Tracks REAL user interaction (mouse, keyboard, touch) and forces logout
 * after 30 minutes of inactivity. API calls from background polling do NOT
 * count as user activity — only physical user input resets the timer.
 */
export const useSessionTimeout = (config: Partial<SessionTimeoutConfig> = {}) => {
  const timeoutMinutes = config.timeoutMinutes ?? CJIS_TIMEOUT_MINUTES;
  const warningMinutes = config.warningMinutes ?? CJIS_WARNING_MINUTES;

  const [state, setState] = useState<SessionTimeoutState>({
    isWarningVisible: false,
    remainingSeconds: 0,
    isExtended: false,
  });

  // Refs for values that should NOT trigger effect re-runs
  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isAudioPlayingRef = useRef<boolean>(false);
  const hasLoggedOutRef = useRef<boolean>(false);

  // Stable force logout — no dependencies, no re-creation
  const forceLogout = useCallback(async () => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    // Clear any running countdown
    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    try {
      await authService.logout();
    } catch {
      // Redirect regardless
    }
    window.location.href = '/login';
  }, []);

  // Reset the inactivity timer — called ONLY by real user input events
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();

    // If warning is showing and user interacts, dismiss it
    setState((prev) => {
      if (!prev.isWarningVisible) return prev;
      return { ...prev, isWarningVisible: false, remainingSeconds: 0 };
    });

    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  // Pause timeout during audio playback (reviewing a 26-min call shouldn't log you out)
  const setAudioPlaying = useCallback((playing: boolean) => {
    isAudioPlayingRef.current = playing;
    if (playing) {
      lastActivityRef.current = Date.now();
    }
  }, []);

  // "Stay Logged In" button handler
  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    setState({ isWarningVisible: false, remainingSeconds: 0, isExtended: true });
    setTimeout(() => {
      setState((prev) => ({ ...prev, isExtended: false }));
    }, 2000);
  }, []);

  // Single stable effect — runs once on mount, cleans up on unmount
  useEffect(() => {
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = warningMinutes * 60 * 1000;

    // Listen for real user input only
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleUserActivity = () => {
      lastActivityRef.current = Date.now();

      // Dismiss warning on real user input
      setState((prev) => {
        if (!prev.isWarningVisible) return prev;
        return { ...prev, isWarningVisible: false, remainingSeconds: 0 };
      });

      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
        warningTimerRef.current = null;
      }
    };

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Periodic check — uses refs only, no state dependencies
    const checkInterval = setInterval(() => {
      // Don't timeout during audio playback
      if (isAudioPlayingRef.current) return;

      // Don't hard-logout mid-upload: window.location.href aborts every in-flight
      // XHR (evidence: files lost in prod). The inactivity clock keeps running, so
      // the first tick after the queue drains still logs out — CJIS 5.5.5 intent
      // holds (drain-then-logout), the evidence transfer is just never corrupted.
      if (isUploadActive()) return;

      const elapsed = Date.now() - lastActivityRef.current;

      if (elapsed >= timeoutMs) {
        // Session expired
        if (!hasLoggedOutRef.current) {
          hasLoggedOutRef.current = true;
          if (warningTimerRef.current) {
            clearInterval(warningTimerRef.current);
            warningTimerRef.current = null;
          }
          authService.logout().catch(() => {}).finally(() => {
            window.location.href = '/login';
          });
        }
        return;
      }

      if (elapsed >= timeoutMs - warningMs) {
        // Show warning and start countdown
        setState((prev) => {
          if (prev.isWarningVisible) return prev; // Already showing

          // Start countdown timer
          if (!warningTimerRef.current) {
            warningTimerRef.current = setInterval(() => {
              // Same upload guard as the main check — never logout mid-transfer.
              if (isUploadActive()) return;

              const currentElapsed = Date.now() - lastActivityRef.current;
              const remaining = Math.ceil((timeoutMs - currentElapsed) / 1000);

              if (remaining <= 0) {
                if (!hasLoggedOutRef.current) {
                  hasLoggedOutRef.current = true;
                  if (warningTimerRef.current) {
                    clearInterval(warningTimerRef.current);
                    warningTimerRef.current = null;
                  }
                  authService.logout().catch(() => {}).finally(() => {
                    window.location.href = '/login';
                  });
                }
              } else {
                setState((s) => ({ ...s, remainingSeconds: remaining }));
              }
            }, 1000);
          }

          const remaining = Math.ceil((timeoutMs - elapsed) / 1000);
          return { ...prev, isWarningVisible: true, remainingSeconds: remaining };
        });
      }
    }, CHECK_INTERVAL_SECONDS * 1000);

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      clearInterval(checkInterval);
      if (warningTimerRef.current) {
        clearInterval(warningTimerRef.current);
      }
    };
  }, [timeoutMinutes, warningMinutes]);

  return {
    ...state,
    extendSession,
    setAudioPlaying,
    updateActivity,
    forceLogout,
  };
};
